import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBeforeUnload, useBlocker, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { getStudyDocument, type StudyHome } from './studyApi';
import { StudyNoteTaskLinks } from './StudyNoteTaskLinks';
import { StudyNoteReview } from './StudyNoteReview';
import { StudyNoteTutor } from './StudyNoteTutor';
import { StudyNoteMaps } from './StudyNoteMaps';
import { notesSavedMotion } from './studyMotionMoments';
import {
  readStudyNote,
  saveStudyNote,
  uploadNotePhoto,
  type NoteUploadState,
  type NoteCrop,
  type NoteWrite,
  type StudyNote,
} from './studyNotesApi';

const formSchema = z.object({
  title: z.string().trim().min(1, 'Give your notes a title.').max(200),
  body: z.string().max(12000),
  topic: z.string().max(120),
  subject_key: z.string(),
  term_id: z.string(),
  class_date: z.union([z.literal(''), z.string().date()]),
});
type NoteForm = z.infer<typeof formSchema>;
type PhotoDraft = {
  mime_type?: string;
  key: string;
  document_id?: string;
  rotation: 0 | 90 | 180 | 270;
  crop?: NoteCrop;
  file?: File;
  preview?: string;
  state: NoteUploadState;
  phase: 'ready' | 'uploading' | 'failed';
  error?: string;
};
const inputClass =
  'mt-1 w-full rounded-xl border border-hairline bg-canvas-pure p-3 text-sm text-ink';
const defaults = (note?: StudyNote): NoteForm => ({
  title: note?.title ?? '',
  body: note?.body ?? '',
  topic: note?.topic ?? '',
  subject_key: note?.subject_key ?? '',
  term_id: note?.term_id ?? '',
  class_date: note?.class_date ?? '',
});
const message = (error: unknown) =>
  error instanceof Error
    ? error.message
    : 'The change could not be saved. Your draft is still here.';

function PhotoThumbnail({
  photo,
  owner,
  page,
}: {
  photo: PhotoDraft;
  owner: string;
  page: number;
}) {
  const original = useQuery({
    queryKey: ['study-note-source', owner, photo.document_id],
    queryFn: () => getStudyDocument(photo.document_id!),
    enabled: Boolean(photo.document_id && !photo.preview),
    retry: false,
    staleTime: 0,
  });
  const url =
    photo.preview ??
    (original.isFetchedAfterMount && !original.isError ? original.data?.read_url : undefined);
  if (original.data?.mime_type === 'application/pdf')
    return <span className="text-xs font-bold text-slate2">PDF</span>;
  return url ? (
    <img src={url} alt={`Page ${page} thumbnail`} className="h-16 w-12 rounded-lg object-contain" />
  ) : (
    <span
      className="flex h-16 w-12 items-center justify-center rounded-lg bg-surface"
      aria-hidden="true"
    >
      {page}
    </span>
  );
}

export function StudyNoteEditor({
  note,
  home,
  owner,
  onClose,
  onCreated,
  savedNotice = false,
  initialFiling,
  initialTaskId,
}: {
  note?: StudyNote;
  home?: StudyHome;
  owner: string;
  onClose: () => void;
  onCreated?: (id: string) => void;
  savedNotice?: boolean;
  initialTaskId?: string;
  initialFiling?: { term_id: string; subject_key: string };
}) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [id] = useState(() => note?.id ?? crypto.randomUUID());
  const [version, setVersion] = useState(note?.version ?? 0);
  const [noteStatus, setNoteStatus] = useState<'active' | 'archived'>(note?.status ?? 'active');
  const [photos, setPhotos] = useState<PhotoDraft[]>(
    () => note?.pages.map((p) => ({ ...p, key: p.document_id, state: {}, phase: 'ready' })) ?? [],
  );
  const [selected, setSelected] = useState(0);
  const [pagesDirty, setPagesDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savedCopy = isLocalStudyExperience()
    ? 'Saved in this experience. Resetting or closing this tab clears it.'
    : 'Saved. Open these notes on your other device to continue.';
  const [notice, setNotice] = useState(savedNotice ? savedCopy : '');
  const [conflict, setConflict] = useState(false);
  const [remote, setRemote] = useState<StudyNote | null>(null);
  const [pending, setPending] = useState<NoteWrite | null>(null);
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const [reviewPending, setReviewPending] = useState(false);
  const [tutorPending, setTutorPending] = useState(false);
  const [mapsPending, setMapsPending] = useState(false);
  const [taskLinksDirty, setTaskLinksDirty] = useState(false);
  const [linkedTasks, setLinkedTasks] = useState(
    note?.linked_task_ids ?? (initialTaskId ? [initialTaskId] : []),
  );
  const [linkedIds, setLinkedIds] = useState(note?.linked_artifact_ids ?? []);
  const linkedSources = useRef(note?.linked_sources ?? []);
  const photoSection = useRef<HTMLElement>(null);
  const previews = useRef(new Set<string>());
  const uploadsInFlight = useRef(false);
  const leavingAfterDeletion = useRef(false);
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    reset,
    setValue,
    formState: { isDirty, errors },
  } = useForm<NoteForm>({
    resolver: zodResolver(formSchema),
    defaultValues: { ...defaults(note), ...(!note ? initialFiling : {}) },
  });
  const dirty =
    isDirty ||
    pagesDirty ||
    taskLinksDirty ||
    Boolean(pending) ||
    reviewPending ||
    tutorPending ||
    mapsPending;
  const busy = saving || photos.some((p) => p.phase === 'uploading');
  const blocked = busy || Boolean(pending) || reviewPending || tutorPending || mapsPending;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !leavingAfterDeletion.current &&
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search),
  );
  useBeforeUnload((event) => {
    if (dirty || busy) {
      event.preventDefault();
      event.returnValue = '';
    }
  });
  useEffect(
    () => () => {
      for (const url of previews.current) URL.revokeObjectURL(url);
    },
    [],
  );
  const source = photos[selected];
  const detail = useQuery({
    queryKey: ['study-note-source', owner, source?.document_id],
    queryFn: () => getStudyDocument(source!.document_id!),
    enabled: Boolean(source?.document_id && !source.preview),
    retry: false,
    staleTime: 0,
  });
  const imageUrl =
    source?.preview ??
    (detail.isFetchedAfterMount && !detail.isError ? detail.data?.read_url : undefined);
  const sourceIsPdf = detail.data?.mime_type === 'application/pdf';
  const terms =
    home?.academic_years?.flatMap((y) =>
      y.terms.map((t) => ({ ...t, label: `${y.school_year} · ${t.label}` })),
    ) ??
    home?.terms ??
    [];
  const selectedTerm = terms.find((t) => t.id === watch('term_id'));
  const subjects = selectedTerm?.subjects ?? home?.subjects ?? [];
  const selectedTermId = watch('term_id');
  const selectedSubjectKey = watch('subject_key');

  function clearMapLinksForFiling() {
    if (!linkedIds.length) return true;
    if (
      !window.confirm(
        'Changing filing removes these mind map links. The original maps remain in Materials. Continue?',
      )
    )
      return false;
    setLinkedIds([]);
    setLinkedTasks([]);
    setPagesDirty(true);
    return true;
  }
  async function upload(photo: PhotoDraft) {
    if (!photo.file || uploadsInFlight.current) return;
    uploadsInFlight.current = true;
    setPhotos((rows) =>
      rows.map((p) => (p.key === photo.key ? { ...p, phase: 'uploading', error: undefined } : p)),
    );
    try {
      const document = await uploadNotePhoto(photo.file, photo.state);
      setPhotos((rows) =>
        rows.map((p) =>
          p.key === photo.key
            ? { ...p, document_id: document.id, phase: 'ready', error: undefined }
            : p,
        ),
      );
    } catch (failure) {
      setPhotos((rows) =>
        rows.map((p) =>
          p.key === photo.key ? { ...p, phase: 'failed', error: message(failure) } : p,
        ),
      );
    } finally {
      uploadsInFlight.current = false;
    }
  }
  async function addFiles(files: File[]) {
    setNotice('');
    setError('');
    if (blocked || uploadsInFlight.current) return;
    if (files.some((f) => !['image/jpeg', 'image/png', 'image/webp'].includes(f.type))) {
      setError('Choose JPEG, PNG or WebP photos. Use Materials for PDFs and other files.');
      return;
    }
    if (files.some((f) => f.size === 0)) {
      setError('A photo is empty. Choose another image before uploading.');
      return;
    }
    if (photos.length + files.length > 20) {
      setError('Save up to 20 photos in one note. Split a longer set into another note.');
      return;
    }
    const next: PhotoDraft[] = files.map((file) => {
      const preview = URL.createObjectURL(file);
      previews.current.add(preview);
      return {
        key: crypto.randomUUID(),
        file,
        preview,
        rotation: 0,
        state: {},
        phase: 'uploading',
      };
    });
    setPhotos((rows) => [...rows, ...next]);
    setPagesDirty(true);
    for (const photo of next) await upload(photo);
  }
  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= photos.length) return;
    setPhotos((rows) => {
      const next = [...rows];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
    setSelected(target);
    setPagesDirty(true);
    setNotice('');
  }
  async function persist(values: NoteForm, status: NoteWrite['status'] = noteStatus) {
    if (
      busy ||
      reviewPending ||
      tutorPending ||
      mapsPending ||
      photos.some((p) => !p.document_id)
    ) {
      setError(
        'Finish or remove failed photo uploads before saving these notes. Uploaded originals remain in Materials.',
      );
      return;
    }
    const snapshot: NoteWrite = pending ?? {
      id,
      request_id: crypto.randomUUID(),
      expected_version: version,
      title: values.title,
      body: values.body,
      topic: values.topic || null,
      subject_key: values.subject_key || null,
      term_id: values.term_id || null,
      class_date: values.class_date || null,
      status,
      linked_artifact_ids: linkedIds,
      linked_task_ids: linkedTasks,
      pages: photos.map((p) => ({
        document_id: p.document_id!,
        rotation: p.rotation,
        ...(p.crop ? { crop: p.crop } : {}),
      })),
    };
    setPending(snapshot);
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await saveStudyNote(snapshot);
      setVersion(result.version);
      setPending(null);
      setPagesDirty(false);
      setTaskLinksDirty(false);
      setConflict(false);
      reset(values);
      setNoteStatus(snapshot.status === 'archived' ? 'archived' : 'active');
      setNotice(savedCopy);
      // M5 (study-motion-feedback-prd.md): the saved pages fly into Materials.
      if (snapshot.status === 'active') notesSavedMotion(photoSection.current);
      await client.invalidateQueries({ queryKey: ['study-notes', owner] });
      await client.invalidateQueries({ queryKey: ['study-note', owner, id] });
      if (snapshot.status === 'deleted') {
        leavingAfterDeletion.current = true;
        onClose();
      } else if (!note && onCreated) {
        leavingAfterDeletion.current = true;
        onCreated(id);
      }
    } catch (failure) {
      setError(message(failure));
      if (
        failure &&
        typeof failure === 'object' &&
        'status' in failure &&
        typeof failure.status === 'number' &&
        failure.status >= 400 &&
        failure.status < 500
      ) {
        setPending(null);
        if ('code' in failure && failure.code === 'STUDY_NOTE_VERSION_CHANGED') setConflict(true);
      }
    } finally {
      setSaving(false);
    }
  }
  if (note && !note.available)
    return (
      <div className="mt-6" role="alert">
        A source is unavailable. These notes cannot expose its content.{' '}
        <button className="underline" onClick={onClose}>
          Back to notes
        </button>
        <button
          className="btn-pill-secondary ml-3"
          disabled={saving}
          onClick={() => {
            if (
              pending ||
              window.confirm('Delete this unavailable note? Original files remain in Materials.')
            )
              void persist(defaults(note), 'deleted');
          }}
        >
          {pending ? 'Retry deletion' : 'Delete unavailable note'}
        </button>
        {error && <p className="mt-3">{error}</p>}
      </div>
    );
  return (
    <>
      {blocker.state === 'blocked' && (
        <div
          role="alertdialog"
          aria-label="Unsaved notes"
          className="mt-5 rounded-3xl border border-hairline bg-surface p-5"
        >
          <p>Your notes have not been saved. Uploaded photos remain in Materials.</p>
          <button className="btn-pill-secondary mt-3" onClick={() => blocker.reset()}>
            Keep editing
          </button>
          <button
            className="btn-pill-secondary ml-2 mt-3"
            disabled={blocked}
            onClick={() => blocker.proceed()}
          >
            Discard draft and leave
          </button>
        </div>
      )}
      <form className="mt-6" onSubmit={handleSubmit((values) => persist(values))}>
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className="btn-pill-secondary" disabled={busy} onClick={onClose}>
            Back to notes
          </button>
          <p role="status" aria-live="polite" className="text-sm font-bold text-slate2">
            {saving
              ? 'Saving…'
              : busy
                ? 'Uploading photos…'
                : pending
                  ? 'Save not confirmed · retry the same change'
                  : dirty
                    ? 'Unsaved changes · this device'
                    : version > 0
                      ? 'Saved'
                      : 'New notes · this device'}
          </p>
          <button
            type="submit"
            className="btn-pill-primary ml-auto"
            disabled={
              busy ||
              reviewPending ||
              tutorPending ||
              conflict ||
              photos.some((p) => !p.document_id)
            }
          >
            {pending ? 'Retry save' : 'Save notes'}
          </button>
        </div>
        {error && (
          <p role="alert" className="mt-4 rounded-2xl bg-surface p-4 text-sm text-ink">
            {error}
          </p>
        )}
        {notice && (
          <p role="status" className="mt-4 text-sm font-bold text-ink">
            {notice}
          </p>
        )}
        {conflict && (
          <div className="mt-4 rounded-3xl border border-hairline bg-surface p-5">
            <p className="font-bold">
              Another device saved a newer version. Your draft is preserved.
            </p>
            <button
              type="button"
              className="btn-pill-secondary mt-3"
              onClick={async () => {
                try {
                  setRemote(await readStudyNote(id));
                } catch (failure) {
                  setError(message(failure));
                }
              }}
            >
              Compare saved version
            </button>
            {remote && (
              <>
                <h2 className="mt-4 font-bold">
                  Saved version {remote.version}: {remote.title}
                </h2>
                <p className="mt-2 whitespace-pre-wrap text-sm">{remote.body}</p>
                <p className="mt-2 text-sm">
                  {remote.pages.length} photos · {remote.topic || 'No topic'} ·{' '}
                  {remote.class_date || 'No class date'} · {remote.status}
                </p>
                <p className="mt-2 text-sm text-ink-soft">
                  Saved task links:{' '}
                  {remote.linked_tasks?.map((task) => task.title).join(', ') || 'None'}.{' '}
                  {taskLinksDirty
                    ? 'Applying your draft also applies your selected task links.'
                    : 'The saved task links will be kept.'}
                </p>
                <button
                  type="button"
                  className="btn-pill-secondary mt-3"
                  disabled={!remote.available}
                  onClick={() => {
                    setVersion(remote.version);
                    if (!taskLinksDirty) setLinkedTasks(remote.linked_task_ids ?? []);
                    setConflict(false);
                    setRemote(null);
                    setError('');
                    setPagesDirty(true);
                  }}
                >
                  Keep my draft and apply it to this version
                </button>
              </>
            )}
          </div>
        )}
        <fieldset disabled={blocked} className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="text-sm font-bold sm:col-span-2 lg:col-span-3">
            Title
            <input
              {...register('title')}
              className={inputClass}
              maxLength={200}
              placeholder="Forces — classroom notes"
            />
            {errors.title && <span role="alert">{errors.title.message}</span>}
          </label>
          <label className="text-sm font-bold">
            Topic
            <input
              {...register('topic')}
              className={inputClass}
              placeholder="Optional"
              maxLength={120}
            />
            {errors.topic && <span role="alert">{errors.topic.message}</span>}
          </label>
          <label className="text-sm font-bold">
            Class date
            <input {...register('class_date')} className={inputClass} type="date" />
            {errors.class_date && <span role="alert">{errors.class_date.message}</span>}
          </label>
          <label className="text-sm font-bold">
            Term
            <select
              aria-label="Term"
              {...register('term_id')}
              onChange={(event) => {
                if (!clearMapLinksForFiling()) return;
                setValue('term_id', event.target.value, { shouldDirty: true });
                setValue('subject_key', '', { shouldDirty: true });
              }}
              className={inputClass}
              value={selectedTermId}
            >
              <option value="">Unfiled · organise later</option>
              {selectedTermId && !terms.some((t) => t.id === selectedTermId) && (
                <option value={selectedTermId}>Saved Term · details unavailable</option>
              )}
              {terms.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold">
            Subject
            <select
              aria-label="Subject"
              {...register('subject_key')}
              onChange={(event) => {
                if (!clearMapLinksForFiling()) return;
                setValue('subject_key', event.target.value, { shouldDirty: true });
              }}
              className={inputClass}
              value={selectedSubjectKey}
            >
              <option value="">Unclassified</option>
              {selectedSubjectKey &&
                !subjects.some((s) => s.subject_key === selectedSubjectKey) && (
                  <option value={selectedSubjectKey}>{selectedSubjectKey} · saved subject</option>
                )}
              {subjects.map((s) => (
                <option key={s.subject_key} value={s.subject_key}>
                  {s.display_name}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
        <div className="mt-6 flex flex-wrap gap-3">
          <label className="btn-pill-secondary cursor-pointer">
            Take a photo
            <input
              aria-label="Take a photo"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="sr-only"
              disabled={blocked}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                void addFiles(files);
              }}
            />
          </label>
          <label className="btn-pill-secondary cursor-pointer">
            Upload photos
            <input
              aria-label="Upload photos"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="sr-only"
              disabled={blocked}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                e.target.value = '';
                void addFiles(files);
              }}
            />
          </label>
          <span className="self-center text-xs text-slate2">
            Up to 20 photos per note · originals stay in Materials
          </span>
        </div>
        <div className="mt-5 grid min-w-0 gap-5 lg:grid-cols-2">
          <section
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              void addFiles(Array.from(event.dataTransfer.files));
            }}
            ref={photoSection}
            aria-label="Source photos"
            className="min-w-0 rounded-3xl border border-hairline bg-canvas-pure p-4"
          >
            <h2 className="font-extrabold text-ink">Original photos · {photos.length}</h2>
            <ol className="mt-3 space-y-3">
              {photos.map((p, index) => (
                <li
                  key={p.key}
                  className="flex flex-wrap items-center gap-2 rounded-xl border border-hairline p-2 text-xs"
                >
                  <button
                    type="button"
                    className="flex min-h-11 items-center gap-2 font-bold underline"
                    onClick={() => setSelected(index)}
                  >
                    <PhotoThumbnail photo={p} owner={owner} page={index + 1} />
                    Page {index + 1}
                  </button>
                  <span>
                    {p.phase === 'ready'
                      ? 'Uploaded'
                      : p.phase === 'failed'
                        ? 'Upload failed'
                        : 'Uploading…'}
                  </span>
                  <button
                    type="button"
                    className="min-h-11 px-2"
                    aria-label={`Move page ${index + 1} earlier`}
                    disabled={blocked || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="min-h-11 px-2"
                    aria-label={`Move page ${index + 1} later`}
                    disabled={blocked || index === photos.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="min-h-11 px-2"
                    disabled={blocked || p.mime_type === 'application/pdf'}
                    onClick={() => {
                      setPhotos((rows) =>
                        rows.map((row) =>
                          row.key === p.key
                            ? {
                                ...row,
                                rotation: ((row.rotation + 90) % 360) as PhotoDraft['rotation'],
                              }
                            : row,
                        ),
                      );
                      setPagesDirty(true);
                    }}
                  >
                    Rotate page {index + 1}
                  </button>
                  <button
                    type="button"
                    className="min-h-11 px-2"
                    disabled={blocked}
                    onClick={() => {
                      const affected = linkedSources.current
                        .filter(
                          (m) =>
                            m.document_id === p.document_id && linkedIds.includes(m.artifact_id),
                        )
                        .map((m) => m.artifact_id);
                      if (
                        affected.length &&
                        !window.confirm(
                          'Removing this page also unlinks its mind maps. Original maps remain in Materials. Continue?',
                        )
                      )
                        return;
                      setLinkedIds((ids) => ids.filter((id) => !affected.includes(id)));
                      setPhotos((rows) => rows.filter((row) => row.key !== p.key));
                      setSelected(0);
                      setPagesDirty(true);
                    }}
                  >
                    Remove page {index + 1}
                  </button>
                  {p.phase === 'failed' && (
                    <>
                      <p role="alert" className="w-full">
                        {p.error}
                      </p>
                      <button
                        type="button"
                        className="min-h-11 underline"
                        disabled={blocked}
                        onClick={() => void upload(p)}
                      >
                        Retry page {index + 1}
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ol>
            {source && sourceIsPdf && detail.data && (
              <div className="mt-4 rounded-2xl border border-hairline bg-canvas p-4">
                <p className="font-bold text-ink">{detail.data.file_name}</p>
                <p className="mt-2 text-sm text-slate2">
                  The original PDF and its annotations stay with this material.
                </p>
                <button
                  type="button"
                  className="btn-pill-secondary mt-3"
                  disabled={dirty}
                  onClick={() =>
                    navigate(`/learn/study/materials/${source.document_id}?original=1`)
                  }
                >
                  Open original PDF
                </button>
              </div>
            )}
            {source && !sourceIsPdf && imageUrl && failedImage !== imageUrl && (
              <div className="mt-4 flex min-h-72 items-center justify-center overflow-hidden rounded-2xl bg-canvas p-8">
                <img
                  className="max-h-96 max-w-full object-contain"
                  style={{
                    transform: `rotate(${source.rotation}deg)`,
                    clipPath: source.crop
                      ? `inset(${source.crop.top * 100}% ${source.crop.right * 100}% ${source.crop.bottom * 100}% ${source.crop.left * 100}%)`
                      : undefined,
                  }}
                  src={imageUrl}
                  onError={() => setFailedImage(imageUrl)}
                  alt={`Original note photo, page ${selected + 1}`}
                />
              </div>
            )}
            {source && !sourceIsPdf && (
              <details className="mt-4 rounded-2xl border border-hairline p-3">
                <summary className="cursor-pointer text-sm font-bold">Crop this photo view</summary>
                <p className="mt-2 text-xs text-slate2">
                  Trim edges for reading. Your original photo stays unchanged.
                </p>
                {(['top', 'right', 'bottom', 'left'] as const).map((edge) => (
                  <label key={edge} className="mt-3 block text-sm">
                    Trim {edge} · {Math.round((source.crop?.[edge] ?? 0) * 100)}%
                    <input
                      className="mt-1 w-full"
                      type="range"
                      min="0"
                      max="40"
                      step="1"
                      disabled={blocked}
                      value={Math.round((source.crop?.[edge] ?? 0) * 100)}
                      onChange={(event) => {
                        const amount = Number(event.target.value) / 100;
                        setPhotos((rows) =>
                          rows.map((p) =>
                            p.key === source.key
                              ? {
                                  ...p,
                                  crop: {
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    left: 0,
                                    ...p.crop,
                                    [edge]: amount,
                                  },
                                }
                              : p,
                          ),
                        );
                        setPagesDirty(true);
                      }}
                    />
                  </label>
                ))}
                <button
                  type="button"
                  className="mt-3 min-h-11 text-sm underline"
                  disabled={blocked}
                  onClick={() => {
                    setPhotos((rows) =>
                      rows.map((p) => (p.key === source.key ? { ...p, crop: undefined } : p)),
                    );
                    setPagesDirty(true);
                  }}
                >
                  Show full original
                </button>
              </details>
            )}
            {(detail.isError || (imageUrl && failedImage === imageUrl)) && (
              <p className="mt-4" role="alert">
                This original could not be loaded.{' '}
                <button
                  type="button"
                  className="underline"
                  disabled={!source?.document_id}
                  onClick={() => {
                    setFailedImage(null);
                    void detail.refetch();
                  }}
                >
                  Retry original
                </button>
              </p>
            )}
            {photos.length === 0 && (
              <p className="mt-4 text-sm text-ink-soft">Add photos, or start with a text note.</p>
            )}
          </section>
          <section className="min-w-0 rounded-3xl border border-hairline bg-canvas-pure p-4">
            <label className="block font-extrabold text-ink">
              My own words
              <textarea
                {...register('body')}
                disabled={blocked}
                maxLength={12000}
                className={`${inputClass} min-h-80 resize-y`}
                placeholder="What did you learn? What is still unclear?"
              />
            </label>
            {errors.body && (
              <p role="alert" className="mt-2">
                {errors.body.message}
              </p>
            )}
            <p className="mt-3 text-xs text-slate2">
              Private learner notes. Saving does not send text to Tutor or read your photos with AI.
            </p>
          </section>
        </div>
        {version > 0 && (
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              className="btn-pill-secondary"
              disabled={blocked || conflict}
              onClick={handleSubmit((values) =>
                persist(values, noteStatus === 'archived' ? 'active' : 'archived'),
              )}
            >
              {noteStatus === 'archived' ? 'Restore to current notes' : 'Archive notes'}
            </button>
            <button
              type="button"
              className="btn-pill-secondary"
              disabled={blocked || conflict}
              onClick={() => {
                if (
                  window.confirm(
                    'Delete these notes, your text, linked reviews and their Tutor history? Original photos remain in Materials. Deleting an original photo also erases notes that reference it.',
                  )
                )
                  void persist(getValues(), 'deleted');
              }}
            >
              Delete notes
            </button>
          </div>
        )}
      </form>
      {note && version > 0 && (
        <StudyNoteMaps
          note={{ ...note, version, linked_artifact_ids: linkedIds }}
          owner={owner}
          enabled={!dirty && !busy && !conflict}
          onPendingChange={setMapsPending}
          onSaved={(nextVersion, ids, sources) => {
            setVersion(nextVersion);
            setLinkedIds(ids);
            linkedSources.current = sources;
          }}
          onOpenSource={(documentId) => {
            const index = photos.findIndex((p) => p.document_id === documentId);
            if (index >= 0) {
              setSelected(index);
              photoSection.current?.scrollIntoView({ block: 'start' });
            }
          }}
        />
      )}
      <StudyNoteTaskLinks
        owner={owner}
        linkedIds={linkedTasks}
        savedTasks={note?.linked_tasks ?? []}
        disabled={blocked || conflict}
        onChange={(ids) => {
          setLinkedTasks(ids);
          setPagesDirty(true);
        }}
      />
      {note && version > 0 && (
        <StudyNoteReview
          note={{ ...note, version }}
          home={home}
          owner={owner}
          enabled={!dirty && !busy && !conflict}
          onPendingChange={setReviewPending}
        />
      )}
      {note && version > 0 && (
        <StudyNoteTutor
          note={{ ...note, version }}
          enabled={!dirty && !busy && !conflict}
          onPendingChange={setTutorPending}
          onOpenSession={(sessionId) => {
            leavingAfterDeletion.current = true;
            navigate(`/learn/study/sessions/${sessionId}`);
          }}
        />
      )}
    </>
  );
}
